import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useState } from "react";
import type { HttpMethod } from "../../shared/models/HttpMethod";
import { useAdminClient } from "./AdminClient";
import {
  type QueryResult,
  QueryResultView,
  type QuerySuccessResult,
} from "./Query";

export type MutationParams<TBody> = {
  path: string;
  method?: HttpMethod;
  body?: TBody;
};

export function useMutation<TBody, TResult>({
  path,
  method,
  body,
}: MutationParams<TBody>): {
  result: QueryResult<TResult>;
  fetch: () => Promise<TResult | undefined>;
} {
  const [updated, setUpdated] = useState<Date | undefined>(new Date());
  const { data, loading, error, fetch } = useAdminClient<TResult>();

  useEffect(() => {
    if (data) setUpdated(new Date());
  }, [data]);

  const doFetch = useCallback(
    () => fetch({ path, method, body }),
    [fetch, path, method, body],
  );

  return {
    fetch: doFetch,
    result: {
      data: data as TResult,
      loading,
      error,
      refresh: doFetch,
      updated,
    },
  };
}

export type MutationProps<TBody, TResult> = MutationParams<TBody> & {
  title: string;
  children?: (queryPage: QuerySuccessResult<TResult>) => JSX.Element;
};

const MutationWrapper = styled("div")(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
}));

const MutationTitle = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  button: {
    marginLeft: "1em",
  },
});

export default function Mutation<TBody, TResult>({
  path,
  method,
  body,
  title,
  children,
}: MutationProps<TBody, TResult>) {
  const [sent, setSent] = useState<Date | undefined>();
  const { result, fetch } = useMutation<TBody, TResult>({ path, method, body });

  useEffect(() => {
    if (sent) fetch();
  }, [sent, fetch]);

  return (
    <MutationWrapper>
      <MutationTitle>
        <h3>{title}</h3>
        <button type="button" onClick={() => setSent(new Date())}>
          Send
        </button>
      </MutationTitle>
      {sent ? (
        <QueryResultView<TResult> queryPage={result}>
          {() =>
            children ? (
              children(result as QuerySuccessResult<TResult>)
            ) : (
              <pre>
                <code>{JSON.stringify(result.data, null, "  ")}</code>
              </pre>
            )
          }
        </QueryResultView>
      ) : null}
    </MutationWrapper>
  );
}
